"use client";

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Circle, Loader2 } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/Dialog";
import { cn } from "@/lib/utils/cn";

import {
  formatLamportsAsSol,
  getInsufficientTraderRentMessage,
  getTraderRegistrationFundingStatus,
  TRADER_REGISTRATION_ACCOUNT_BYTES,
  TRADER_REGISTRATION_MAX_POSITIONS,
} from "../traderRegistrationFunding";

import { type OnboardingStep, useOnboardingStore } from "./onboardingStore";

type Pane =
  | { id: "access"; kind: "access" }
  | {
      id: "account_setup" | "finish_here";
      kind: "working";
      title: string;
      description: string;
      minVisibleMs?: number;
    }
  | { id: "error"; kind: "error" };

const TRACKER_STEPS = [
  { id: "referral", title: "Referral" },
  { id: "set_up_account", title: "Set up account" },
  { id: "finish_sign_in", title: "Finish sign-in" },
] as const;

const getPane = (step: OnboardingStep): Pane | null => {
  switch (step) {
    case "accessCodeEntry":
      return { id: "access", kind: "access" };
    case "activating":
      return {
        id: "account_setup",
        kind: "working",
        title: "Setting up your account",
        description: "Preparing your Phoenix trader account.",
        minVisibleMs: 750,
      };
    case "done":
      return {
        id: "finish_here",
        kind: "working",
        title: "Finishing sign-in",
        description: "Applying your session and closing this window.",
      };
    case "error":
      return { id: "error", kind: "error" };
    case "idle":
    default:
      return null;
  }
};

const getCurrentStepId = (step: OnboardingStep): string | null => {
  switch (step) {
    case "accessCodeEntry":
    case "error":
      return "referral";
    case "activating":
      return "set_up_account";
    case "done":
      return "finish_sign_in";
    case "idle":
    default:
      return null;
  }
};

export const OnboardingModal = () => {
  const dismissModal = useOnboardingStore((state) => state.dismissModal);
  const isOpen = useOnboardingStore((state) => state.isModalOpen);
  const step = useOnboardingStore((state) => state.step);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && dismissModal()}>
      <DialogContent
        className="max-w-[90vw] rounded-2xl border border-white/10 bg-card text-white shadow-2xl sm:max-w-[500px]"
        padding="none"
      >
        <DialogHeader title="Onboarding" className="sr-only" />
        <div className="p-6">
          <div className="flex flex-col gap-4">
            <OnboardingTracker />
            <AnimatedPaneBody step={step} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AnimatedPaneBody = ({ step }: { step: OnboardingStep }) => {
  const pane = getPane(step);
  const [displayedPane, setDisplayedPane] = useState(pane);
  const displayedAtRef = useRef(Date.now());

  useEffect(() => {
    if (pane?.id === displayedPane?.id) return;

    const elapsedMs = Date.now() - displayedAtRef.current;
    const minVisibleMs =
      displayedPane?.kind === "working" ? (displayedPane.minVisibleMs ?? 0) : 0;
    const remainingMs = Math.max(0, minVisibleMs - elapsedMs);

    const timeoutId = window.setTimeout(() => {
      setDisplayedPane(pane);
      displayedAtRef.current = Date.now();
    }, remainingMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [displayedPane, pane]);

  if (displayedPane === null) return null;

  switch (displayedPane.kind) {
    case "access":
      return <AccessCodePane />;
    case "working":
      return (
        <WorkingPane
          title={displayedPane.title}
          description={displayedPane.description}
        />
      );
    case "error":
      return <ErrorPane />;
  }
};

const OnboardingTracker = () => {
  const step = useOnboardingStore((state) => state.step);
  const currentStepId = getCurrentStepId(step);
  if (currentStepId === null) return null;

  const currentIndex =
    step === "done"
      ? TRACKER_STEPS.length - 0.5
      : TRACKER_STEPS.findIndex(
          (trackerStep) => trackerStep.id === currentStepId
        );

  return (
    <div className="relative w-full">
      <div className="absolute left-0 top-0 flex w-full items-center justify-center">
        <div className="absolute top-0 h-[2px] w-full translate-y-[-50%] bg-muted" />
        <div
          className="absolute left-0 top-0 h-[2px] translate-y-[-50%] bg-primary transition-all duration-300"
          style={{
            width: `${((currentIndex + 0.5) / TRACKER_STEPS.length) * 100}%`,
          }}
        />
        <div
          className="relative grid w-full translate-y-[-50%]"
          style={{
            gridTemplateColumns: `repeat(${TRACKER_STEPS.length}, minmax(0, 1fr))`,
          }}
        >
          {TRACKER_STEPS.map((trackerStep, index) => (
            <div
              key={trackerStep.id}
              className={cn(
                "flex items-center justify-center text-primary",
                index > currentIndex && "text-muted"
              )}
            >
              {index < currentIndex ? (
                <CheckCircle2
                  className="size-4"
                  fill="currentColor"
                  stroke="#0b1220"
                />
              ) : (
                <Circle className="size-2" fill="currentColor" />
              )}
            </div>
          ))}
        </div>
      </div>

      <ol
        className="grid w-full items-center justify-center"
        style={{
          gridTemplateColumns: `repeat(${TRACKER_STEPS.length}, minmax(0, 1fr))`,
        }}
      >
        {TRACKER_STEPS.map((trackerStep) => (
          <li
            key={trackerStep.id}
            className="flex items-center justify-center p-2"
          >
            <span
              className={cn(
                "rounded-lg px-2 py-1.5 text-center text-xs font-medium leading-none text-muted-foreground transition-colors",
                trackerStep.id === currentStepId && "bg-primary/15 text-primary"
              )}
            >
              {trackerStep.title}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
};

const AccessCodePane = () => {
  const accessCode = useOnboardingStore((state) => state.accessCode);
  const pubkey = useOnboardingStore((state) => state.pubkey);
  const setAccessCode = useOnboardingStore((state) => state.setAccessCode);
  const startActivation = useOnboardingStore((state) => state.startActivation);
  const fundingQuery = useQuery({
    queryKey: ["traderRegistrationFunding", pubkey],
    enabled: pubkey !== null,
    queryFn: () => {
      if (pubkey === null) {
        throw new Error("Wallet is not connected");
      }
      return getTraderRegistrationFundingStatus(pubkey);
    },
    staleTime: 15_000,
  });

  const insufficientRentMessage =
    fundingQuery.data !== undefined
      ? getInsufficientTraderRentMessage(fundingQuery.data)
      : null;
  const requiredFundingSol =
    fundingQuery.data !== undefined
      ? formatLamportsAsSol(fundingQuery.data.requiredFundingLamports)
      : null;
  const txFeeSol =
    fundingQuery.data !== undefined
      ? formatLamportsAsSol(fundingQuery.data.txFeeLamports)
      : null;
  const disabled = fundingQuery.isLoading || insufficientRentMessage !== null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disabled) return;
    startActivation();
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <label htmlFor="accessCode" className="sr-only">
        Referral code
      </label>
      <input
        id="accessCode"
        type="text"
        placeholder="Referral code (optional)"
        value={accessCode}
        onChange={(event) => setAccessCode(event.target.value)}
        className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-3 text-base text-white transition-colors placeholder:text-white/40 focus:border-primary focus:outline-none"
      />
      <Alert
        variant="info"
        className="border border-white/10 bg-white/[0.04] text-white/75"
      >
        {requiredFundingSol === null || txFeeSol === null
          ? "Checking the SOL required for a new Phoenix trader account."
          : `A new Phoenix trader with ${TRADER_REGISTRATION_MAX_POSITIONS} positions requires ${requiredFundingSol} SOL to fund a ${TRADER_REGISTRATION_ACCOUNT_BYTES.toLocaleString()} byte trader account and cover an estimated ${txFeeSol} SOL transaction fee.`}
      </Alert>
      {fundingQuery.data?.requiresNewTraderAccount === false && (
        <Alert
          variant="success"
          className="border border-emerald-300/20 bg-emerald-300/10 text-emerald-100"
        >
          This wallet already has a Phoenix trader account, so new trader rent
          is not required.
        </Alert>
      )}
      {fundingQuery.isError && (
        <Alert
          variant="warn"
          className="border border-amber-300/20 bg-amber-300/10 text-amber-100"
        >
          Could not verify this wallet&apos;s SOL balance. You can continue, but
          the wallet may reject account setup if it cannot fund trader rent.
        </Alert>
      )}
      {insufficientRentMessage !== null && (
        <Alert
          variant="error"
          className="border border-red-500/25 bg-red-500/10 text-red-100"
          icon={<AlertTriangle className="size-4" />}
        >
          {insufficientRentMessage}
        </Alert>
      )}
      <Button type="submit" disabled={disabled} variant="primary" size="lg">
        Continue
      </Button>
    </form>
  );
};

const WorkingPane = ({
  description,
  title,
}: {
  description: string;
  title: string;
}) => (
  <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center">
    <Loader2 className="size-5 animate-spin" />
    <div className="flex max-w-sm flex-col gap-1">
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="text-sm text-white/65">{description}</p>
    </div>
  </div>
);

const ErrorPane = () => {
  const clearError = useOnboardingStore((state) => state.clearError);
  const error = useOnboardingStore((state) => state.error);

  if (error === null) return null;

  return (
    <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-red-500/25 bg-red-500/10 p-8 text-center">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-red-500/15 text-red-300">
        <AlertTriangle className="size-4" />
      </div>
      <div className="flex max-w-sm flex-col gap-1">
        <h3 className="text-base font-semibold">{error.title}</h3>
        {error.description !== undefined && error.description.trim() !== "" ? (
          <p className="text-sm text-white/75">{error.description}</p>
        ) : null}
      </div>
      <Button onClick={clearError}>Go back</Button>
    </div>
  );
};
